import Page from "./page";
import * as models from "powerbi-models";

declare const powerbi: any;

interface EmbedReport {
    embedUrl: string;
    reportId: string;
    reportName: string;
}

interface EmbedData {
    embedReports: EmbedReport[];
    embedToken: string;
    tokenExpiry: string;
}

export default class ReferenceStatisticsPage extends Page {
    private reportContainerWrapper: HTMLElement | null;
    private pageTitle: string;

    constructor(protected body: JQuery<HTMLBodyElement>) {
        super();
        this.reportContainerWrapper = this.body.find("#report-container-wrapper")[0] ?? null;
        this.pageTitle = this.reportContainerWrapper?.dataset.pageTitle || "";
    }

    init(): void {
        super.init();
        if (!this.reportContainerWrapper) {
            console.error("#report-container-wrapper element not found in DOM.");
            return;
        }
        this.loadPowerBiReports();
    }

    private async loadPowerBiReports(): Promise<void> {
        try {
            const embedData: EmbedData = await this.fetchEmbedData();

            if (!embedData.embedReports || embedData.embedReports.length === 0) {
                console.error("No embed reports returned from backend.", embedData);
                return;
            }

            this.reportContainerWrapper!.innerHTML = "";

            const tabBar = this.createTabs(embedData.embedReports);
            this.reportContainerWrapper!.appendChild(tabBar);

            embedData.embedReports.forEach((reportInfo, index) => {
                const reportDiv = this.createReportContainer(index, index === 0);
                this.reportContainerWrapper!.appendChild(reportDiv);
                this.embedReport(reportDiv, reportInfo, embedData.embedToken);
            });

            this.attachTabEvents(tabBar, embedData.embedReports);
        } catch (error: any) {
            console.error("Failed to load Power BI reports:", error);
            this.showError(error.responseText || error.message);
        }
    }

    private createTabs(reports: EmbedReport[]): HTMLUListElement {
        const tabBar = document.createElement("ul");
        tabBar.className = "report-tabs";
        tabBar.style.display = "flex";
        tabBar.style.gap = "1rem";
        tabBar.style.listStyle = "none";
        tabBar.style.marginBottom = "1rem";
        tabBar.style.padding = "0";

        reports.forEach((_report, index) => {
            const tab = document.createElement("li");
            tab.innerText = this.pageTitle || `Report ${index + 1}`;
            tab.dataset.tab = `${index}`;
            tab.style.padding = "0.5rem 1rem";
            tab.style.borderRadius = "6px";
            tab.style.border = "1px solid #ccc";
            tab.style.cursor = "pointer";

            if (index === 0) {
                tab.style.background = "#e7e7e7";
                tab.style.fontWeight = "bold";
            }

            tabBar.appendChild(tab);
        });

        return tabBar;
    }

    private attachTabEvents(tabBar: HTMLElement, reports: EmbedReport[]) {
        const reportsCount = reports.length;
        tabBar.querySelectorAll("li").forEach((tab) => {
            tab.addEventListener("click", () => {
                const idx = parseInt(tab.dataset.tab || "0", 10);
                this.activateTab(tabBar, idx, reportsCount);
            });
        });
    }

    private activateTab(tabBar: HTMLElement, index: number, reportsCount: number) {
        tabBar.querySelectorAll("li").forEach((t) => {
            t.style.background = "";
            t.style.fontWeight = "";
        });

        const activeTab = tabBar.querySelector(`li[data-tab="${index}"]`) as HTMLElement;
        if (activeTab) {
            activeTab.style.background = "#e7e7e7";
            activeTab.style.fontWeight = "bold";
        }

        //Show selected report container only
        for (let i = 0; i < reportsCount; i++) {
            const div = document.getElementById(`report-container-${i}`);
            if (div) {
                div.style.display = i === index ? "block" : "none";
            }
        }
    }

    private createReportContainer(
        index: number,
        visible: boolean
    ): HTMLDivElement {
        const div = document.createElement("div");
        div.id = `report-container-${index}`;
        div.style.width = "100%";
        div.style.height = "120vh";
        div.style.marginBottom = "2rem";
        div.style.display = visible ? "block" : "none";
        return div;
    }

    private embedReport(
        container: HTMLElement,
        reportInfo: EmbedReport,
        token: string
    ) {
        powerbi.bootstrap(container, {type: "report"});

        const report = powerbi.embed(container, {
            type: "report",
            tokenType: models.TokenType.Embed,
            accessToken: token,
            embedUrl: reportInfo.embedUrl,
            settings: {
                background: models.BackgroundType.Transparent,
                layoutType: models.LayoutType.Custom,
                panes: {
                    pageNavigation: {
                        visible: false
                    }
                }
            },
        });

        report.off("error");
        report.on("error", (event: any) => {
            console.error(
                `Power BI Error in report ${reportInfo.reportName}:`,
                event.detail
            );
        });
        report.on("loaded", () =>
            console.log(`Report loaded: ${reportInfo.reportName}`)
        );
        report.on("rendered", () =>
            console.log(`Report rendered: ${reportInfo.reportName}`)
        );
    }

    private fetchEmbedData(): Promise<EmbedData> {
        return new Promise((resolve, reject) => {
            $.ajax({
                type: "GET",
                url: `${window.ACC.config.encodedContextPath}/powerbi/embedinfo`,
                dataType: "json",
                success: (data) => resolve(data),
                error: (err) => reject(err),
            });
        });
    }

    private showError(message?: string) {
        $(".embed-container").hide();
        const errorContainer = $(".error-container");
        errorContainer.show();
        const errorMessage = message ?? "An unknown error occurred.";
        errorContainer.html(
            `<strong>Error Details:</strong><br/>${errorMessage.split("\n").join("<br/>")}`
        );
    }
}
